import { api } from '../../core/network/api';

/** GET /api/referral/me — the player's own referral code + stats (Settings → Referral). */
export interface ReferralInfo {
  code: string;
  referredCount: number;
  claimed: boolean;
  reward: number;
}

export const fetchReferralInfo = () => api.get<ReferralInfo>('/referral/me');

/** POST /api/referral/claim — redeem a pending code for the signup bonus. 400s on
 *  invalid/self/already-claimed/stale-account codes (see consumePendingReferral). */
export const claimReferral = (code: string) =>
  api.post<{ ok: true; reward: number }>('/referral/claim', { code });

const PENDING_KEY = 'referral.pending';

/**
 * Capture the `?ref=CODE` query param at boot, before auth exists. Runs on
 * every load (landing, /login, /register) since the core auth screens are
 * untouchable and can't carry this themselves. Strips the param immediately
 * so it doesn't linger in the URL or get re-captured on a later navigation.
 */
export function captureReferralCode(): void {
  const params = new URLSearchParams(location.search);
  const ref = params.get('ref');
  if (!ref) return;
  localStorage.setItem(PENDING_KEY, ref);
  params.delete('ref');
  const query = params.toString();
  history.replaceState(null, '', location.pathname + (query ? `?${query}` : '') + location.hash);
}

let attempted = false;

/**
 * Claim-once orchestration — call once the authenticated game UI is up.
 * Module-level `attempted` guard survives repeated calls within the same
 * page load (e.g. a remounted screen); any failure (invalid/self/already
 * claimed/account too old, or the endpoint not deployed yet) clears the
 * stored code too — we never retry-spam. Returns the credited reward on
 * success, so the caller can toast/refresh, or null if nothing happened.
 */
export async function consumePendingReferral(): Promise<number | null> {
  if (attempted) return null;
  const code = localStorage.getItem(PENDING_KEY);
  if (!code) return null;
  attempted = true;
  try {
    const { reward } = await claimReferral(code);
    localStorage.removeItem(PENDING_KEY);
    return reward;
  } catch {
    localStorage.removeItem(PENDING_KEY);
    return null;
  }
}
